import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from 'react-query';
import { AuthService, UsersService } from 'services';
import {
  disableUserMFA,
  enableUserMFA,
  getCurrentUser,
  getUser,
  getUserIdentity,
  getUserPreference,
  resetUserMFA,
  updateUser,
  updateUserPreference,
  UpdateUserPreferencePayload,
} from 'services/users';
import { UserInfo, UserPreference } from 'models/user';
import { useAuth0 } from '@auth0/auth0-react';
import { CONSTANTS } from 'constants/common';
import { getPrimaryAccountByUserId, IAccount } from 'services/account';
import { useSpoof } from './useNavigate';

export const useCurrentUser = (
  queryOptions?: UseQueryOptions<{
    loginUser: UserInfo;
    currentUser: UserInfo;
  }>,
) => {
  const { isAuthenticated } = useAuth0();
  const token = AuthService.userToken();
  const { spoofUserID } = useSpoof();
  const { data, ...rest } = useQuery<
    {
      loginUser: UserInfo;
      currentUser: UserInfo;
    },
    unknown
  >(
    ['currentUser'],
    async () => {
      const loginUser = await getCurrentUser();
      AuthService.updateCurrentUser(loginUser);
      let currentUser = loginUser;
      if (spoofUserID) {
        currentUser = await getUser(spoofUserID);
      }

      return {
        loginUser,
        currentUser,
      };
    },
    {
      enabled: isAuthenticated && !!token,
      ...queryOptions,
    },
  );
  return {
    currentUser: data?.currentUser,
    loginUser: data?.loginUser,
    ...rest,
  };
};

export const useCurrentUserV1 = (
  queryOptions?: UseQueryOptions<{
    loginUser: UserInfo;
    currentUser: UserInfo;
    accounts: IAccount[];
  }>,
) => {
  const { isAuthenticated } = useAuth0();
  const { spoofUserID } = useSpoof();
  const { data, ...rest } = useQuery<
    { loginUser: UserInfo; currentUser: UserInfo; accounts: IAccount[] },
    unknown
  >(
    'currentUser',
    async () => {
      const loginUser = await getCurrentUser();
      let currentUser = loginUser;
      if (spoofUserID) {
        currentUser = await getUser(spoofUserID);
      }
      const account = await getPrimaryAccountByUserId(currentUser?.id);
      return {
        loginUser,
        currentUser,
        accounts: [account],
      };
    },
    {
      ...queryOptions,
      enabled: isAuthenticated,
    },
  );
  return {
    currentUser: data?.currentUser,
    loginUser: data?.loginUser,
    accounts: data?.accounts,
    ...rest,
  };
};

export const useCurrentUserRole = (): {
  isAdmin: boolean;
  isLoginUserAdmin: boolean;
  isLoginUserSuperAdmin: boolean;
} => {
  const { spoofUserID } = useSpoof();
  const { currentUser, loginUser } = useCurrentUser();
  let isAdmin = currentUser?.role !== CONSTANTS.USER_ROLES.CUSTOMER;
  let isLoginUserAdmin = loginUser?.role !== CONSTANTS.USER_ROLES.CUSTOMER;
  const isLoginUserSuperAdmin =
    loginUser?.role === CONSTANTS.USER_ROLES.SUPER_ADMIN;
  if (spoofUserID) {
    isAdmin = false;
    isLoginUserAdmin = true;
  }
  return {
    isAdmin,
    isLoginUserAdmin,
    isLoginUserSuperAdmin,
  };
};

export const useUser = (
  id: string | undefined,
  options: UseQueryOptions<UserInfo> = {},
) => {
  const { data, ...rest } = useQuery<UserInfo>(
    ['user', id],
    () => getUser(id!),
    {
      enabled: !!id,
      ...options,
    },
  );
  return {
    user: data,
    ...rest,
  };
};

interface UpdateVariables {
  id?: string;
  data: any;
}
export const useUpdateUser = (
  queryProps?: Omit<
    UseMutationOptions<unknown, unknown, UpdateVariables>,
    'mutationFn'
  >,
) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    ({ id, data }: { id?: string; data: any }) => updateUser(data, id),
    queryProps,
  );
  return {
    updateUser: mutate,
    updateUserAsync: mutateAsync,
    ...rest,
  };
};

interface UserIdentityVariable {
  id: string;
  ssn: string;
}
export const useUpdateUserIdentity = (
  queryProps?: Omit<
    UseMutationOptions<unknown, unknown, UserIdentityVariable>,
    'mutationFn'
  >,
) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    ({ id, ssn }: { id: string; ssn: string }) =>
      UsersService.updateIdentityByUserId(id, { ssn }),
    queryProps,
  );

  return {
    updateUserIdentity: mutate,
    updateUserIdentityAsync: mutateAsync,
    ...rest,
  };
};

interface UserRoleVariable {
  id: string;
  role: string;
}

export const useUpdateUserRole = (
  queryProps?: Omit<
    UseMutationOptions<unknown, unknown, UserRoleVariable>,
    'mutationFn'
  >,
) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    ({ id, role }: { id: string; role: string }) =>
      UsersService.updateUserRole(id, { role }),
    queryProps,
  );

  return {
    updateUserRole: mutate,
    updateUserRoleAsync: mutateAsync,
    ...rest,
  };
};

interface UserEmailVariable { 
  id: string;
  email: string;
}

export const useUpdateUserEmail = (
  queryProps?: Omit<
    UseMutationOptions<unknown, unknown, UserEmailVariable>,
    'mutationFn'
  >,
) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    ({ id, email }: { id: string; email: string }) =>
      UsersService.updateUserEmail(id, { email }),
    queryProps,
  );

  return {
    updateUserEmail: mutate,
    updateUserEmailAsync: mutateAsync,
    ...rest,
  };
};

export interface UserIdentity {
  first5: string;
  last4: string;
}

export const useUserIdentityById = (
  id?: string,
  queryProps?: Omit<UseQueryOptions<UserIdentity>, 'queryKey' | 'queryFn'>,
) => {
  const { data, ...rest } = useQuery<UserIdentity, unknown>(
    ['user', 'identity', id],
    () => getUserIdentity(id),
    { enabled: !!id, ...queryProps },
  );
  return { userIdentity: data, ...rest };
};

export const useResetUserMFA = (userId: string) => {
  const { mutate, mutateAsync, ...rest } = useMutation(() =>
    resetUserMFA(userId),
  );
  return {
    resetUserMFA: mutate,
    resetUserMFAAsync: mutateAsync,
    ...rest,
  };
};

export const useDisableUserMFA = (userId: string) => {
  const { mutate, mutateAsync, ...rest } = useMutation(() =>
    disableUserMFA(userId),
  );
  return {
    disableUserMFA: mutate,
    disableUserMFAAsync: mutateAsync,
    ...rest,
  };
};

export const useEnableUserMFA = (userId: string) => {
  const { mutate, mutateAsync, ...rest } = useMutation(() =>
    enableUserMFA(userId),
  );
  return {
    enableUserMFA: mutate,
    enableUserMFAAsync: mutateAsync,
    ...rest,
  };
};

export const useUserPreference = (userId: string) => {
  const { data, ...rest } = useQuery<UserPreference>(
    ['userPreference', userId],
    () => getUserPreference(userId),
  );

  return {
    userPreference: data,
    ...rest,
  };
};

export const useUpdateUserPreference = (userId: string) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    (payload: UpdateUserPreferencePayload) =>
      updateUserPreference(userId, payload),
  );
  return {
    updateUserPreference: mutate,
    updateUserPreferenceAsync: mutateAsync,
    ...rest,
  };
};
